import { FC, useState } from 'react';
import { useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
import { QcsTable } from '../common/basic/QcsTable';
import { QcsTableHead } from '../common/basic/QcsTableHead';
import { QcsTableRow } from '../common/basic/QcsTableRow';
import { QcsTableCell } from '../common/basic/QcsTableCell';
import { QcsTableBody } from '../common/basic/QcsTableBody';
import { QcsCheckbox } from '../common/basic/QcsCheckbox';
import { QcsInfoTooltip } from '../common/basic/QcsInfoTooltip';
import { QcsFormHelperText } from '../common/basic/QcsFormHelperText';
import { QcsTypography } from '../common/basic/QcsTypography';
import { QcsButton } from '../common/basic/QcsButton';
import QcsAlert from '../common/basic/QcsAlert';
import { Search } from '../common/Search';
import {
  filterScopeResources,
  formatMissingRequirement,
  getMissingRequirements,
  groupMissingRequirements,
  getScopeAccesses,
  getScopeAccessLabel,
  getScopeLabel,
  getScopeResourceLabel,
  getScopeResources,
} from './integrationUtils';
import { ApiScopeDto } from '@api/models/apiClient/ApiScopeDto';

interface Props {
  catalogue: ApiScopeDto[];
}

export const IntegrationScopeMatrix: FC<Props> = ({ catalogue }) => {
  const { t } = useTranslation();
  const { values, errors, submitCount, setFieldValue } = useFormikContext<{ scopes: string[] }>();

  const [filter, setFilter] = useState('');

  const accesses = getScopeAccesses(catalogue);
  const shownResources = filterScopeResources(t, getScopeResources(catalogue), filter);
  const missingRequirements = groupMissingRequirements(getMissingRequirements(values.scopes, catalogue));

  const handleToggle = (scope: string, checked: boolean) => {
    setFieldValue('scopes', checked ? [...values.scopes, scope] : values.scopes.filter((x) => x !== scope));
  };

  const getHint = (scope: ApiScopeDto) =>
    [
      scope.endpoints.length > 0 && [t('integration.apiAccess.scopes.allows'), ...scope.endpoints].join('\n'),
      scope.requires.length > 0 &&
        [t('integration.apiAccess.scopes.requires'), ...scope.requires.map((x) => getScopeLabel(t, x, catalogue))].join('\n'),
    ]
      .filter((x) => !!x)
      .join('\n\n');

  return (
    <div>
      {/* Enter in the filter must not submit the surrounding form. */}
      <Box
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem', mb: 1 }}
        onKeyDown={(e) => e.key === 'Enter' && e.preventDefault()}
      >
        <Search defaultValue="" onChanged={setFilter} />
        <QcsTypography variant="body2" sx={{ color: 'text.secondary' }}>
          {t('integration.apiAccess.scopes.selectedCount', { count: values.scopes.length })}
        </QcsTypography>
      </Box>
      <QcsTable>
        <QcsTableHead>
          <QcsTableRow>
            <QcsTableCell>{t('integration.apiAccess.scopes.title')}</QcsTableCell>
            {accesses.map((access) => (
              <QcsTableCell key={access} align="center" sx={{ width: '8rem' }}>
                {getScopeAccessLabel(t, access)}
              </QcsTableCell>
            ))}
          </QcsTableRow>
        </QcsTableHead>
        <QcsTableBody>
          {shownResources.length === 0 && (
            <QcsTableRow>
              <QcsTableCell colSpan={accesses.length + 1} sx={{ color: 'text.secondary' }}>
                {t('integration.apiAccess.scopes.noMatch')}
              </QcsTableCell>
            </QcsTableRow>
          )}
          {shownResources.map((resource) => (
            <QcsTableRow key={resource}>
              <QcsTableCell>{getScopeResourceLabel(t, resource)}</QcsTableCell>
              {accesses.map((access) => {
                const scope = catalogue.find((x) => x.resource === resource && x.access === access);
                const hint = scope && getHint(scope);

                return (
                  <QcsTableCell key={access} align="center">
                    {scope && (
                      //The checkbox sits in the middle column, so it stays centred - aligned with the rows above - with or without the hint.
                      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'center' }}>
                        <Box sx={{ gridColumn: 2 }}>
                          <QcsCheckbox checked={values.scopes.includes(scope.scope)} onChange={(_, checked) => handleToggle(scope.scope, checked)} />
                        </Box>
                        {hint && (
                          <Box sx={{ justifySelf: 'start', display: 'flex' }}>
                            <QcsInfoTooltip title={hint} />
                          </Box>
                        )}
                      </Box>
                    )}
                  </QcsTableCell>
                );
              })}
            </QcsTableRow>
          ))}
        </QcsTableBody>
      </QcsTable>
      {missingRequirements.map((requirement) => (
        <QcsAlert
          key={requirement.missing.join()}
          severity="warning"
          sx={{ width: '100%', mt: 1 }}
          action={
            <QcsButton
              color="inherit"
              onClick={() => setFieldValue('scopes', [...values.scopes, ...requirement.missing.filter((x) => !values.scopes.includes(x))])}
            >
              {t('integration.apiAccess.scopes.allowRequired')}
            </QcsButton>
          }
        >
          {formatMissingRequirement(t, requirement, catalogue)}
        </QcsAlert>
      ))}
      {/* A missing requirement is already explained by its warning above. */}
      {submitCount > 0 && !!errors.scopes && missingRequirements.length === 0 && (
        <QcsFormHelperText error>{errors.scopes as string}</QcsFormHelperText>
      )}
    </div>
  );
};
